import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CircularProgress, Typography } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { Wrapper } from "components/wrapper";
import MunicipalPortalAAppService from "application/platform/municipal-portal-service";
import { ModuleShortcutCard, ResultGroup, ResultGroupTitle, ResultItem, ResultItemSubtitle, ResultItemTitle } from "./search-results.styles";

interface ResultEntry {
  id: number;
  title: string;
  subtitle?: string;
  route: string;
}

interface ResultSection {
  label: string;
  items: ResultEntry[];
}

const MODULE_SHORTCUTS: { keywords: string[]; label: string; route: string }[] = [
  { keywords: ["licitacao", "licitacoes", "licitação", "licitações"], label: "Licitações", route: "/licitacoes" },
  { keywords: ["convenio", "convenios", "convênio", "convênios"], label: "Convênios", route: "/convenios" },
  { keywords: ["emenda", "emendas", "emenda parlamentar", "emendas parlamentares"], label: "Emendas Parlamentares", route: "/emendas-parlamentares" },
  { keywords: ["contrato", "contratos"], label: "Contratos", route: "/contratos" },
  { keywords: ["obra", "obras"], label: "Obras", route: "/obras" },
];

function findModuleShortcut(term: string) {
  const normalized = term.trim().toLowerCase();
  return MODULE_SHORTCUTS.find((m) => m.keywords.includes(normalized));
}

function highlightMatch(text: string, term: string): React.ReactNode {
  if (!term) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  if (parts.length === 1) return text;

  return parts.map((part, index) =>
    part.toLowerCase() === term.toLowerCase() ? <mark key={index}>{part}</mark> : part
  );
}

export function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const termo = searchParams.get("termo") || "";

  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState<ResultSection[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (!termo) {
      setSections([]);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const request = { page: 0, pageSize: 5, term: termo };

        const [noticias, licitacoes, convenios, emendas, contratos] = await Promise.all([
          MunicipalPortalAAppService.listNews(request as any),
          MunicipalPortalAAppService.listLicitacoes(request as any),
          MunicipalPortalAAppService.listConvenios(request as any),
          MunicipalPortalAAppService.listEmendasParlamentares(request as any),
          MunicipalPortalAAppService.listContratos(request as any),
        ]);

        const result: ResultSection[] = [
          {
            label: "Notícias",
            items: (noticias?.items || []).map((n) => ({ id: n.id, title: n.title, subtitle: n.subtitle, route: `/noticias/${n.id}` })),
          },
          {
            label: "Licitações",
            items: (licitacoes?.items || []).map((l) => ({ id: l.id, title: `Licitação ${l.numLicitacao}`, subtitle: l.textoObjetoLicitacao, route: `/licitacoes/${l.id}` })),
          },
          {
            label: "Convênios",
            items: (convenios?.items || []).map((c) => ({ id: c.id, title: `Convênio ${c.numeroConvenio}`, subtitle: c.textoObjetoConvenio, route: `/convenios/${c.id}` })),
          },
          {
            label: "Emendas Parlamentares",
            items: (emendas?.items || []).map((e) => ({ id: e.id, title: `Emenda ${e.numeroEmenda}`, subtitle: e.textoObjetoEmenda, route: `/emendas-parlamentares/${e.id}` })),
          },
          {
            label: "Contratos",
            items: (contratos?.items || []).map((c) => ({ id: c.id, title: `Contrato ${c.numContrato}`, subtitle: c.textoObjetoContrato, route: `/contratos/${c.id}` })),
          },
        ].filter((section) => section.items.length > 0);

        setSections(result);
      } catch (error) {
        console.log(error);
        setSections([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [termo]);

  const totalResults = sections.reduce((acc, section) => acc + section.items.length, 0);
  const moduleShortcut = termo ? findModuleShortcut(termo) : undefined;

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Resultados da busca" },
        ]}
      />
      <PageTitle>Resultados da busca</PageTitle>
      <Wrapper>
        {!termo && (
          <Typography variant="body1">Digite um termo no campo de busca para pesquisar no site.</Typography>
        )}

        {termo && loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <CircularProgress />
          </div>
        )}

        {termo && !loading && (
          <>
            {moduleShortcut && (
              <ModuleShortcutCard onClick={() => navigate(moduleShortcut.route)}>
                <span>Ver todos os registros de {moduleShortcut.label}</span>
                <span className="arrow">→</span>
              </ModuleShortcutCard>
            )}

            <Typography variant="body1" style={{ marginBottom: 24 }}>
              {totalResults > 0
                ? `${totalResults} resultado(s) encontrado(s) para "${termo}"`
                : `Nenhum resultado encontrado para "${termo}"`}
            </Typography>

            {sections.map((section) => (
              <ResultGroup key={section.label}>
                <ResultGroupTitle>{section.label}</ResultGroupTitle>
                {section.items.map((item) => (
                  <ResultItem key={item.id} onClick={() => navigate(item.route)}>
                    <ResultItemTitle>{highlightMatch(item.title, termo)}</ResultItemTitle>
                    {item.subtitle && <ResultItemSubtitle>{highlightMatch(item.subtitle, termo)}</ResultItemSubtitle>}
                  </ResultItem>
                ))}
              </ResultGroup>
            ))}
          </>
        )}
      </Wrapper>
    </>
  );
}
